"use client"

import { ChevronLeftMini, ChevronRightMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import { clx } from "@modules/common/components/ui"
import Image from "next/image"
import { useCallback, useEffect, useRef, useState } from "react"

type LightboxProps = {
  images: HttpTypes.StoreProductImage[]
  initialIndex: number
  onClose: (index: number) => void
}

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ZOOM_STEP = 0.4
const SWIPE_THRESHOLD = 50

const Lightbox = ({ images, initialIndex, onClose }: LightboxProps) => {
  const [index, setIndex] = useState(initialIndex)
  const [zoom, setZoom] = useState(MIN_ZOOM)
  const touchStartX = useRef<number | null>(null)

  const paginate = useCallback(
    (direction: number) => {
      if (images.length < 2) {
        return
      }

      setZoom(MIN_ZOOM)
      setIndex((prev) => (prev + direction + images.length) % images.length)
    },
    [images.length]
  )

  // Keyboard: Escape closes, arrows paginate.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose(index)
      } else if (e.key === "ArrowRight") {
        paginate(1)
      } else if (e.key === "ArrowLeft") {
        paginate(-1)
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [index, paginate, onClose])

  // Lock page scroll while the lightbox is open.
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = original
    }
  }, [])

  const handleWheel = (e: React.WheelEvent) => {
    setZoom((prev) =>
      Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, prev - Math.sign(e.deltaY) * ZOOM_STEP)
      )
    )
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) {
      return
    }

    const delta = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current
    touchStartX.current = null

    if (zoom === MIN_ZOOM && Math.abs(delta) > SWIPE_THRESHOLD) {
      paginate(delta < 0 ? 1 : -1)
    }
  }

  const currentImage = images[index]

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label="Fullscreen image gallery"
      data-testid="lightbox"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <span
          role="status"
          aria-label={`Image ${index + 1} of ${images.length}`}
          className="text-sm text-grey-30 tabular-nums"
          data-testid="lightbox-counter"
        >
          {index + 1}/{images.length}
        </span>
        <button
          type="button"
          onClick={() => onClose(index)}
          aria-label="Close fullscreen view"
          className="p-2 text-grey-30 hover:text-white transition-colors"
          data-testid="lightbox-close"
        >
          <XMark aria-hidden="true" />
        </button>
      </div>
      <div
        className="relative flex-1 overflow-hidden"
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={clx(
            "absolute inset-4 small:inset-12 transition-transform duration-200 ease-out",
            zoom > MIN_ZOOM ? "cursor-zoom-out" : "cursor-zoom-in"
          )}
          style={{ transform: `scale(${zoom})` }}
          onClick={() => setZoom((prev) => (prev > MIN_ZOOM ? MIN_ZOOM : 2))}
          data-testid="lightbox-image"
        >
          {currentImage?.url && (
            <Image
              src={currentImage.url}
              alt={`Product image ${index + 1}`}
              fill
              sizes="100vw"
              className="object-contain object-center select-none"
              draggable={false}
              priority
            />
          )}
        </div>
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => paginate(-1)}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-grey-20 hover:bg-black/70 hover:text-white transition-colors"
              data-testid="lightbox-prev"
            >
              <ChevronLeftMini aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => paginate(1)}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-grey-20 hover:bg-black/70 hover:text-white transition-colors"
              data-testid="lightbox-next"
            >
              <ChevronRightMini aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default Lightbox
